document.addEventListener('DOMContentLoaded', () => {
  const root = document.querySelector('[data-product-section]')
  if (!root) return

  // Sticky details column
  const stickyCol = root.querySelector('[data-product-sticky]')
  const desktopMq = window.matchMedia('(min-width: 1024px)')

  const updateStickyTop = () => {
    if (!stickyCol) return
    if (!desktopMq.matches) {
      stickyCol.style.top = ''
      return
    }
    const overflow = stickyCol.offsetHeight - window.innerHeight
    stickyCol.style.top = overflow > 0 ? `${-overflow}px` : '0px'
  }

  updateStickyTop()
  window.addEventListener('resize', updateStickyTop)
  desktopMq.addEventListener('change', updateStickyTop)

  if (stickyCol && 'ResizeObserver' in window) {
    new ResizeObserver(updateStickyTop).observe(stickyCol)
  }

  // Accordions
  root.querySelectorAll('[data-accordion]').forEach((accordion) => {
    const trigger = accordion.querySelector('[data-accordion-trigger]')
    if (!trigger) return

    trigger.addEventListener('click', () => {
      const open = trigger.getAttribute('aria-expanded') !== 'true'
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false')
    })
  })

  // Mobile gallery carousel indicator
  const gallery = root.querySelector('[data-product-gallery]')
  const setupIndicator = (galleryEl) => {
    const track = galleryEl.querySelector('[data-gallery-track]')
    const bars = Array.from(galleryEl.querySelectorAll('[data-gallery-bar]'))
    if (!track || !bars.length) return

    const getStep = () => {
      const slide = track.querySelector('[data-gallery-slide]')
      return slide ? slide.offsetWidth : track.clientWidth
    }

    const update = () => {
      const step = getStep()
      const index = step ? Math.round(track.scrollLeft / step) : 0
      bars.forEach((bar, i) => {
        bar.classList.toggle('bg-black-200', i === index)
        bar.classList.toggle('bg-black-12', i !== index)
      })
    }

    let ticking = false
    track.addEventListener('scroll', () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        update()
        ticking = false
      })
    })
  }

  if (gallery) setupIndicator(gallery)

  // Gallery image loading
  const setupImageLoading = (galleryEl) => {
    const images = Array.from(galleryEl.querySelectorAll('[data-gallery-image]'))
    if (!images.length) return

    if (window.gsap) {
      const { gsap } = window

      images.forEach((image) => {
        if (image.complete) return

        gsap.set(image, { autoAlpha: 0 })
        const show = () => gsap.to(image, { autoAlpha: 1, duration: 0.8, ease: 'power2.out' })

        image.addEventListener('load', show, { once: true })
        image.addEventListener('error', show, { once: true })
      })
    }

    window.addEventListener('load', () => {
      images.forEach((image) => {
        if (image.complete) return
        image.fetchPriority = 'low'
        image.loading = 'eager'
      })
    })
  }

  if (gallery) setupImageLoading(gallery)

  // Fullscreen gallery lightbox
  const setupLightbox = (galleryEl) => {
    const lightbox = galleryEl.querySelector('[data-gallery-lightbox]')
    const lightboxImage = lightbox && lightbox.querySelector('[data-gallery-lightbox-image]')
    const hiresImage = lightbox && lightbox.querySelector('[data-gallery-lightbox-hires]')
    const closeBtn = lightbox && lightbox.querySelector('[data-gallery-lightbox-close]')
    if (!lightbox || !lightboxImage) return

    document.body.appendChild(lightbox)

    const lockScroll = () => {
      document.documentElement.style.overflow = 'hidden'
      document.body.classList.add('overflow-hidden')
      if (window.lenis) window.lenis.stop()
    }

    const unlockScroll = () => {
      document.documentElement.style.overflow = ''
      document.body.classList.remove('overflow-hidden')
      if (window.lenis) window.lenis.start()
    }

    let pendingLoad = null

    const open = (image) => {
      const preview = image.currentSrc || image.src
      const full = image.dataset.fullSrc
      const alt = image.alt || ''

      if (pendingLoad) {
        pendingLoad.onload = null
        pendingLoad = null
      }
      if (hiresImage) {
        hiresImage.classList.remove('opacity-100')
        hiresImage.classList.add('opacity-0')
        hiresImage.removeAttribute('src')
      }

      lightboxImage.alt = alt
      lightboxImage.src = preview

      if (hiresImage && full && full !== preview) {
        const hi = new Image()
        pendingLoad = hi
        hi.onload = () => {
          if (pendingLoad !== hi) return
          hiresImage.alt = alt
          hiresImage.src = full
          requestAnimationFrame(() => {
            hiresImage.classList.remove('opacity-0')
            hiresImage.classList.add('opacity-100')
          })
          pendingLoad = null
        }
        hi.src = full
      }

      lightbox.scrollTop = 0
      lightbox.classList.remove('opacity-0', 'invisible')
      lightbox.classList.add('opacity-100', 'visible')
      lightbox.setAttribute('aria-hidden', 'false')
      lockScroll()
    }

    const close = () => {
      lightbox.classList.remove('opacity-100', 'visible')
      lightbox.classList.add('opacity-0', 'invisible')
      lightbox.setAttribute('aria-hidden', 'true')
      if (pendingLoad) {
        pendingLoad.onload = null
        pendingLoad = null
      }
      unlockScroll()
    }

    galleryEl.querySelectorAll('[data-gallery-image]').forEach((image) => {
      image.addEventListener('click', () => open(image))
    })

    if (closeBtn) closeBtn.addEventListener('click', close)

    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox) close()
    })

    document.addEventListener('keyup', (event) => {
      if (event.code === 'Escape' && lightbox.getAttribute('aria-hidden') === 'false') close()
    })
  }

  if (gallery) setupLightbox(gallery)

  // On load animation
  const media = root.querySelector('[data-product-media]')
  const revealItems = Array.from(root.querySelectorAll('[data-product-reveal]'))
  const title = root.querySelector('[data-product-title]')
  const targets = [media, ...revealItems].filter(Boolean)

  if (window.gsap && (targets.length || title)) {
    const { gsap } = window

    if (targets.length) gsap.set(targets, { opacity: 0, y: 40 })

    const play = (titleLines) => {
      const tl = gsap.timeline({ delay: 0.2 })

      if (titleLines?.length) {
        tl.from(titleLines, { yPercent: 120, opacity: 0, duration: 0.9, ease: 'power3.out', stagger: 0.12 }, 0)
      }

      if (targets.length) {
        tl.to(targets, { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', stagger: 0.12 }, 0)
      }

      return tl
    }

    const start = () => {
      title?.classList.remove('opacity-0')

      if (!title || !window.SplitText) {
        play()
        return
      }

      const split = window.SplitText.create(title, { type: 'lines', mask: 'lines' })
      play(split.lines).eventCallback('onComplete', () => split.revert())
    }

    if (document.fonts?.ready) document.fonts.ready.then(start)
    else start()
  } else {
    targets.forEach((el) => el.classList.remove('opacity-0'))
    title?.classList.remove('opacity-0')
  }
})
